import { array, bool, i64, publicKey, struct, u8, u64, u128 } from "@coral-xyz/borsh";
import type { PublicKey } from "@solana/web3.js";
import type BN from "bn.js";

export type VaultLayout = {
  authority: PublicKey;
  token_program: PublicKey;
  pda_token_account: PublicKey;
  pda: PublicKey;
  nonce: number;
  info_nonce: number;
  reward_a_nonce: number;
  reward_b_nonce: number;
  swap_to_nonce: number;
  total_vault_balance: BN;
  info_account: PublicKey;
  lp_token_account: PublicKey;
  lp_token_mint: PublicKey;
  reward_a_account: PublicKey;
  reward_b_account: PublicKey;
  swap_to_account: PublicKey;
  total_vlp_shares: BN;
  tulip_reward_per_slot: BN;
  tulip_reward_per_share: BN;
  tulip_reward_end_slot: BN;
  last_interaction_slot: BN;
};

export const VAULT_LAYOUT = struct<VaultLayout>([
  array(u8(), 8),
  publicKey("authority"),
  publicKey("token_program"),
  publicKey("pda_token_account"),
  publicKey("pda"),
  u8("nonce"),
  u8("info_nonce"),
  u8("reward_a_nonce"),
  u8("reward_b_nonce"),
  u8("swap_to_nonce"),
  u64("total_vault_balance"),
  publicKey("info_account"),
  publicKey("lp_token_account"),
  publicKey("lp_token_mint"),
  publicKey("reward_a_account"),
  publicKey("reward_b_account"),
  publicKey("swap_to_account"),
  u64("total_vlp_shares"),
  array(u8(), 449),
  u64("tulip_reward_per_slot"),
  u128("tulip_reward_per_share"),
  u64("tulip_reward_end_slot"),
  u64("last_interaction_slot"),
]);

export type OrcaVaultLayout = {
  authority: PublicKey;
  pda: PublicKey;
  pda_nonce: number;
  account_nonce: number;
  compound_authority: PublicKey;
  user_farm_addr: PublicKey;
  user_farm_nonce: number;
  total_vault_balance: BN;
  total_vlp_shares: BN;
  farm_key: PublicKey;
  deposits_paused: boolean;
  withdraws_paused: boolean;
  farm_token_account: PublicKey;
  swap_token_account: PublicKey;
  reward_token_account: PublicKey;
  precision_factor: BN;
  last_compound_time: BN;
  compound_interval: BN;
  swap_tokenA_account: PublicKey;
  swap_tokenB_account: PublicKey;
  slippage_tolerance: number;
  controller_fee: BN;
  platform_fee: BN;
  fee_recipient: PublicKey;
  host_fee_recipient: PublicKey;
  usdc_token_account: PublicKey;
};

export const ORCA_VAULT_LAYOUT = struct<OrcaVaultLayout>([
  array(u8(), 8),
  publicKey("authority"),
  publicKey("pda"),
  u8("pda_nonce"),
  u8("account_nonce"),
  publicKey("compound_authority"),
  publicKey("user_farm_addr"),
  u8("user_farm_nonce"),
  u64("total_vault_balance"),
  u64("total_vlp_shares"),
  u8("farm_key"),
  bool("deposits_paused"),
  bool("withdraws_paused"),
  publicKey("farm_token_account"),
  publicKey("swap_token_account"),
  publicKey("reward_token_account"),
  u64("precision_factor"),
  i64("last_compound_time"),
  i64("compound_interval"),
  publicKey("swap_tokenA_account"),
  publicKey("swap_tokenB_account"),
  u8("slippage_tolerance"),
  u64("controller_fee"),
  u64("platform_fee"),
  publicKey("fee_recipient"),
  publicKey("host_fee_recipient"),
  publicKey("usdc_token_account"),
  array(u8(), 545),
]);

export type LendingObligationLayout = {
  version: number;
  lastUpdateSlot: {
    slot: number;
    stale: boolean;
  };
  lendingMarket: PublicKey;
  owner: PublicKey;
  borrowedValue: BN;
  vaultShares: BN;
  lpTokens: BN;
  coinDeposits: BN;
  pcDeposits: BN;
  depositsMarketValue: BN;
  lpDecimals: number;
  coinDecimals: number;
  pcDecimals: number;
  depositsLen: number;
  borrowsLen: number;
  obligationBorrowOne: {
    borrowReserve: PublicKey;
    cumulativeBorrowRateWads: BN;
    borrowedAmountWads: BN;
    marketValue: BN;
  };
  obligationBorrowTwo: {
    borrowReserve: PublicKey;
    cumulativeBorrowRateWads: BN;
    borrowedAmountWads: BN;
    marketValue: BN;
  };
};

export const LENDING_OBLIGATION_LAYOUT = struct<LendingObligationLayout>([
  u8("version"),
  struct([u64("slot"), bool("stale")], "lastUpdateSlot"),
  publicKey("lendingMarket"),
  publicKey("owner"),
  u128("borrowedValue"), // decimal
  u64("vaultShares"), // decimal
  u64("lpTokens"), // decimal
  u64("coinDeposits"), // decimal
  u64("pcDeposits"), // decimal
  u128("depositsMarketValue"), // decimal
  u8("lpDecimals"),
  u8("coinDecimals"),
  u8("pcDecimals"),
  u8("depositsLen"),
  u8("borrowsLen"),
  struct(
    [
      publicKey("borrowReserve"),
      u128("cumulativeBorrowRateWads"), // decimal
      u128("borrowedAmountWads"), // decimal
      u128("marketValue"), // decimal
    ],
    "obligationBorrowOne",
  ),
  struct(
    [
      publicKey("borrowReserve"),
      u128("cumulativeBorrowRateWads"), // decimal
      u128("borrowedAmountWads"), // decimal
      u128("marketValue"), // decimal
    ],
    "obligationBorrowTwo",
  ),
]);
